/**
 * Edge gateway health — the Devices screen's Gateways tab.
 *
 * REST AND POLLED, deliberately, and this is the existing precedent
 * rather than a shortcut: `useIssues` does the same for the same kind
 * of data (a remote thing reporting a problem). Making gateways a live
 * Zenoh category would touch the SSE classifier, the reducers, the
 * storage manager and the published-keys guard test, for data that
 * changes on the order of a bridge restart.
 *
 * The row shape below is what the Devices screen has ALWAYS wanted —
 * `GatewayRow` with Connectivity and Last Heartbeat columns has existed
 * since that screen was written, backed by a hardcoded empty array,
 * because nothing produced it. This is the producer.
 */
import { useQuery } from '@tanstack/react-query';
import { useSafeSiteId } from '../../state/useActiveSite';
import { getRuntimeConfig } from '../../config/runtime-config';
import { getRequestBearerToken } from '../../auth/request-auth';

export const GATEWAYS_KEY = 'edge-gateways' as const;
export const gatewaysKey = (siteId: string) => [GATEWAYS_KEY, siteId] as const;

/** What one edge service is doing.
 *
 *  `unreachable` is the state the box CANNOT report about itself: its
 *  last-will covers a service dying, but a dropped WAN link publishes
 *  nothing at all, so the server derives this one from silence. */
export type GatewayState = 'up' | 'degraded' | 'down' | 'unreachable';

export interface EdgeGateway {
  id: string;
  site: string;
  service: string;
  state: GatewayState;
  detail: Record<string, unknown>;
  vendor: string | null;
  adapter: string | null;
  /** The PLATFORM's receipt clock, not the box's own — an edge box is
   *  remote and its clock drifts, and rendering its own timestamp would
   *  show a "last heartbeat" in the future. */
  last_seen_ms: number;
}

// Fetched directly rather than through `v1-client.ts`, which carries
// the generated v1 contract surface: this is an api-local shape, the
// same reason `useNotificationPrefs` fetches its deliverability probe
// here.
async function fetchGateways(site: string): Promise<EdgeGateway[]> {
  const token = getRequestBearerToken();
  const res = await fetch(
    `${getRuntimeConfig().apiBaseUrl}/api/v1/sites/${encodeURIComponent(site)}/gateways`,
    { cache: 'no-store', headers: token ? { Authorization: `Bearer ${token}` } : {} },
  );
  if (!res.ok) throw new Error(`HTTP ${res.status}`);
  return (await res.json()) as EdgeGateway[];
}

export function useGateways() {
  const siteId = useSafeSiteId();
  return useQuery<EdgeGateway[]>({
    queryKey: gatewaysKey(siteId),
    queryFn: () => fetchGateways(siteId),
    enabled: Boolean(siteId),
    // Matches `useIssues`. The server ages a silent gateway to
    // `unreachable` on its own sweep, so polling faster would not
    // detect an outage sooner — it would only ask more often.
    staleTime: 30_000,
    refetchInterval: 30_000,
  });
}
